import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, TextField, Alert,
    CircularProgress, Paper, Grid
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuthStore } from '../store/authStore';
import { useThemeContext } from './contexts/ThemeContext';
import { urlBase } from '../config';
import { logoutApi } from '../security/AuthService';
import { useNavigate } from 'react-router-dom';

export default function EnforcedTwoFactorModal() {
    const navigate = useNavigate();
    const { mode } = useThemeContext();
    const darkTheme = mode === 'dark';

    const {
        token,
        isAuthenticated,
        twoFactorEnabled,
        mfaSetupEnforced,
        updateAccessToken,
        setTwoFactorEnabled,
        logout,
        getRefreshToken
    } = useAuthStore();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [step, setStep] = useState('PASSWORD'); // 'PASSWORD' | 'QR' | 'BACKUP_CODES'
    const [setupPassword, setSetupPassword] = useState('');
    const [setupData, setSetupData] = useState({ secretKey: '', qrCodeDataUri: '' });
    const [verifyCode, setVerifyCode] = useState('');
    const [backupCodes, setBackupCodes] = useState([]);
    const [pendingTokens, setPendingTokens] = useState(null);
    const [copiedSecret, setCopiedSecret] = useState(false);
    const [copiedCodes, setCopiedCodes] = useState(false);

    // Wyświetlaj tylko gdy zalogowany, 2FA nieaktywne ORAZ termin minął (mfaSetupEnforced)
    const isVisible = isAuthenticated && !twoFactorEnabled && mfaSetupEnforced;

    useEffect(() => {
        if (!isVisible) {
            setStep('PASSWORD');
            setSetupPassword('');
            setVerifyCode('');
            setError('');
            setPendingTokens(null);
        }
    }, [isVisible]);

    const authHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };

    const startSetup = async () => {
        if (!token) return;
        if (!setupPassword) {
            setError('Wprowadź aktualne hasło do konta, aby wygenerować kod QR.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const resp = await fetch(`${urlBase}/api/auth/2fa/setup`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ password: setupPassword })
            });
            if (resp.ok) {
                const data = await resp.json();
                setSetupData(data);
                setSetupPassword('');
                setStep('QR');
            } else {
                const errText = await resp.text();
                setError(errText || 'Nie udało się zainicjować konfiguracji 2FA.');
            }
        } catch (err) {
            setError('Błąd połączenia z serwerem.');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmSetup = async () => {
        if (!verifyCode || verifyCode.trim().length !== 6) {
            setError('Wprowadź poprawny 6-cyfrowy kod weryfikacyjny z aplikacji');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const resp = await fetch(`${urlBase}/api/auth/2fa/confirm`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ code: verifyCode.trim() })
            });
            if (resp.ok) {
                const data = await resp.json();
                setBackupCodes(data.backupCodes || []);
                if (data.accessToken) {
                    setPendingTokens({
                        accessToken: data.accessToken,
                        refreshToken: data.refreshToken
                    });
                }
                setStep('BACKUP_CODES');
            } else {
                const errText = await resp.text();
                setError(errText || 'Niepoprawny kod weryfikacyjny. Spróbuj ponownie.');
            }
        } catch (err) {
            setError('Błąd połączenia z serwerem.');
        } finally {
            setLoading(false);
        }
    };

    const handleFinish = () => {
        if (pendingTokens?.accessToken) {
            updateAccessToken(pendingTokens.accessToken, pendingTokens.refreshToken);
        }
        setTwoFactorEnabled(true);
    };

    const handleLogout = async () => {
        try {
            const refreshToken = getRefreshToken();
            await logoutApi(token, refreshToken);
        } catch (e) {}
        logout();
        sessionStorage.clear();
        localStorage.removeItem('pelikan-auth');
        navigate('/SignIn');
    };

    const copyToClipboard = (text, type) => {
        navigator.clipboard.writeText(text);
        if (type === 'secret') {
            setCopiedSecret(true);
            setTimeout(() => setCopiedSecret(false), 2000);
        } else {
            setCopiedCodes(true);
            setTimeout(() => setCopiedCodes(false), 2000);
        }
    };

    const downloadBackupCodes = () => {
        const text = `PELIKAN - JEDNORAZOWE KODY ZAPASOWE 2FA\nData: ${new Date().toLocaleString()}\n\n` +
            backupCodes.map((c, i) => `${i + 1}. ${c}`).join('\n') +
            '\n\nKażdy kod może zostać użyty tylko raz. Zachowaj ten plik w bezpiecznym miejscu.';
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `pelikan-kody-zapasowe-2fa-${new Date().toISOString().slice(0, 10)}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    if (!isVisible) return null;

    return (
        <Dialog
            open={isVisible}
            disableEscapeKeyDown
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    p: { xs: 1, sm: 2 },
                    backgroundColor: darkTheme ? '#161B22' : '#FFFFFF',
                    backgroundImage: 'none',
                    border: '1px solid',
                    borderColor: darkTheme ? 'rgba(255, 255, 255, 0.12)' : '#E2E8F0',
                    boxShadow: '0 24px 60px rgba(0,0,0,0.4)'
                }
            }}
        >
            <DialogTitle sx={{ pb: 1, pt: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                    sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                        color: '#FFFFFF',
                        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.35)'
                    }}
                >
                    <LockIcon />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: darkTheme ? '#F8FAFC' : '#0F172A', fontSize: '18px' }}>
                        Wymagana konfiguracja 2FA
                    </Typography>
                    <Typography variant="caption" sx={{ color: darkTheme ? '#94A3B8' : '#64748B', display: 'block', fontSize: '12px' }}>
                        Upłynął 30-dniowy okres przejściowy na włączenie weryfikacji dwuetapowej
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
                        <CircularProgress size={44} />
                        <Typography variant="body2" sx={{ color: darkTheme ? '#94A3B8' : '#64748B' }}>
                            Trwa przetwarzanie...
                        </Typography>
                    </Box>
                ) : step === 'PASSWORD' ? (
                    <Box sx={{ mt: 1 }}>
                        <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }}>
                            Dostęp do systemu jest zablokowany do momentu ukończenia konfiguracji 2FA.
                            Potwierdź swoje hasło, aby wygenerować kod QR dla aplikacji Google Authenticator.
                        </Alert>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: darkTheme ? '#F8FAFC' : '#0F172A', mb: 1 }}>
                            Aktualne hasło do konta:
                        </Typography>
                        <TextField
                            fullWidth
                            autoFocus
                            type="password"
                            placeholder="Wprowadź aktualne hasło"
                            value={setupPassword}
                            onChange={(e) => setSetupPassword(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && setupPassword) startSetup(); }}
                            sx={{ mb: 1 }}
                        />
                    </Box>
                ) : step === 'QR' ? (
                    <Box sx={{ mt: 1 }}>
                        <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }}>
                            Dostęp do systemu jest zablokowany do momentu ukończenia konfiguracji 2FA.
                            Zeskanuj kod QR aplikacją Google Authenticator, aby kontynuować.
                        </Alert>

                        <Typography variant="body2" sx={{ fontWeight: 600, color: darkTheme ? '#F8FAFC' : '#0F172A', mb: 1.5 }}>
                            1. Zeskanuj kod QR w Google Authenticator:
                        </Typography>

                        {setupData.qrCodeDataUri && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        backgroundColor: '#FFFFFF',
                                        borderRadius: 2,
                                        border: '1px solid #E2E8F0',
                                        display: 'inline-block'
                                    }}
                                >
                                    <img
                                        src={setupData.qrCodeDataUri}
                                        alt="Kod QR 2FA"
                                        style={{ width: 190, height: 190, display: 'block' }}
                                    />
                                </Paper>
                            </Box>
                        )}

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="caption" sx={{ color: darkTheme ? '#94A3B8' : '#64748B', display: 'block', mb: 0.5 }}>
                                Nie możesz zeskanować kodu? Wpisz ten klucz w aplikacji:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        px: 1.5,
                                        py: 0.8,
                                        flex: 1,
                                        backgroundColor: darkTheme ? 'rgba(255,255,255,0.06)' : '#F1F5F9',
                                        fontFamily: 'monospace',
                                        fontWeight: 700,
                                        letterSpacing: '0.12em',
                                        fontSize: '13px',
                                        color: darkTheme ? '#F8FAFC' : '#0F172A',
                                        borderRadius: 1.5,
                                        textAlign: 'center'
                                    }}
                                >
                                    {setupData.secretKey}
                                </Paper>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => copyToClipboard(setupData.secretKey, 'secret')}
                                    startIcon={copiedSecret ? <CheckCircleIcon color="success" /> : <ContentCopyIcon />}
                                    sx={{ textTransform: 'none', borderRadius: 1.5 }}
                                >
                                    {copiedSecret ? 'Skopiowano' : 'Kopiuj'}
                                </Button>
                            </Box>
                        </Box>

                        <Typography variant="body2" sx={{ fontWeight: 600, color: darkTheme ? '#F8FAFC' : '#0F172A', mb: 1 }}>
                            2. Wpisz 6-cyfrowy kod z aplikacji Google Authenticator:
                        </Typography>

                        <TextField
                            fullWidth
                            autoFocus
                            placeholder="000000"
                            value={verifyCode}
                            onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmSetup(); }}
                            inputProps={{
                                maxLength: 6,
                                style: {
                                    textAlign: 'center',
                                    fontSize: '24px',
                                    fontWeight: 700,
                                    letterSpacing: '0.3em',
                                    fontFamily: 'monospace'
                                }
                            }}
                            sx={{ mb: 1 }}
                        />
                    </Box>
                ) : step === 'BACKUP_CODES' ? (
                    <Box sx={{ mt: 1 }}>
                        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                            Weryfikacja dwuetapowa została włączona! Zapisz poniższe jednorazowe kody zapasowe.
                        </Alert>

                        <Typography variant="body2" sx={{ color: darkTheme ? '#CBD5E1' : '#475569', fontSize: '13px', mb: 2 }}>
                            Kody zapasowe pozwolą Ci zalogować się do systemu, jeśli zgubisz telefon lub utracisz dostęp do aplikacji Google Authenticator.
                        </Typography>

                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                backgroundColor: darkTheme ? 'rgba(255,255,255,0.04)' : '#F8FAFC',
                                border: '1px solid',
                                borderColor: darkTheme ? 'rgba(255,255,255,0.08)' : '#E2E8F0',
                                borderRadius: 2,
                                mb: 2
                            }}
                        >
                            <Grid container spacing={1}>
                                {backupCodes.map((c, i) => (
                                    <Grid item xs={6} key={i}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontFamily: 'monospace',
                                                fontWeight: 600,
                                                fontSize: '13px',
                                                color: darkTheme ? '#F8FAFC' : '#0F172A',
                                                textAlign: 'center',
                                                py: 0.5,
                                                backgroundColor: darkTheme ? 'rgba(255,255,255,0.05)' : '#FFFFFF',
                                                borderRadius: 1
                                            }}
                                        >
                                            {c}
                                        </Typography>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>

                        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={downloadBackupCodes}
                                sx={{ borderRadius: 1.5, textTransform: 'none' }}
                            >
                                Pobierz kody (.txt)
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={copiedCodes ? <CheckCircleIcon color="success" /> : <ContentCopyIcon />}
                                onClick={() => copyToClipboard(backupCodes.join('\n'), 'codes')}
                                sx={{ borderRadius: 1.5, textTransform: 'none' }}
                            >
                                {copiedCodes ? 'Skopiowano' : 'Kopiuj wszystkie'}
                            </Button>
                        </Box>
                    </Box>
                ) : null}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                    color="inherit"
                    onClick={handleLogout}
                    startIcon={<LogoutIcon />}
                    sx={{ textTransform: 'none', color: '#EF4444' }}
                >
                    Wyloguj się
                </Button>

                {step === 'PASSWORD' && (
                    <Button
                        variant="contained"
                        onClick={startSetup}
                        disabled={loading || !setupPassword}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            backgroundColor: '#3B82F6',
                            '&:hover': { backgroundColor: '#2563EB' }
                        }}
                    >
                        Dalej – wygeneruj kod QR
                    </Button>
                )}

                {step === 'QR' && (
                    <Button
                        variant="contained"
                        onClick={handleConfirmSetup}
                        disabled={loading || verifyCode.length !== 6}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            backgroundColor: '#10B981',
                            '&:hover': { backgroundColor: '#059669' }
                        }}
                    >
                        Zatwierdź i aktywuj 2FA
                    </Button>
                )}

                {step === 'BACKUP_CODES' && (
                    <Button
                        variant="contained"
                        onClick={handleFinish}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            backgroundColor: '#10B981',
                            '&:hover': { backgroundColor: '#059669' }
                        }}
                    >
                        Przejdź do systemu Pelikan
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
